import React, { useCallback, useState } from 'react';
import { TextField, Button, Box, Alert, Card, CardContent, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ImageIcon from '@mui/icons-material/Image';
import { VisuallyHiddenInput, formStyle } from './../styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import ConfirmDialog from '../components/ConfirmDialog';


const PostEditForm = ({ handleSubmit, post, handlePostChange, photoSrcs, handleImagesChange, handleCancel }) => {
    const [changed, setChanged] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

    const changeImagesInternal = useCallback((e) => {
        const images = [];
        for (const file of e.target.files) {
            const fileURL = URL.createObjectURL(file);
            images.push({ file, fileURL });
        }

        handleImagesChange(images);
        setChanged(true);
    }, [handleImagesChange]);

    const removeImagesInternal = useCallback(() => {
        handleImagesChange([]);
        setChanged(true);
    }, [handleImagesChange]);

    const handlePostTextChange = useCallback((e) => {
        handlePostChange({ ...post, text: e.target.value });
        setChanged(true);
    }, [post, handlePostChange]);

    return (
        <form onSubmit={handleSubmit} style={formStyle}>
            {post?.status && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Status: <b>{post.status}</b>
                </Alert>
            )}

            {/* Post Content Section */}
            <Card sx={{ mb: 2, width: '100%' }}>
                <CardContent sx={{ padding: '24px !important' }}>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <DescriptionIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">Content</Typography>
                    </Box>
                    <TextField
                        label="Text"
                        value={post?.text || ""}
                        onChange={handlePostTextChange}
                        fullWidth
                        multiline
                        minRows={8}
                        maxRows={8}
                        disabled={post?.status === 'published'}
                        error={post?.text && post.text.length > 300}
                        helperText={post?.text && post.text.length > 300 ? 'Text is too long (max 300 characters)' : ' '}
                        autoFocus
                    />
                </CardContent>
            </Card>

            {/* Schedule Section */}
            <Card sx={{ mb: 2, width: '100%' }}>
                <CardContent sx={{ padding: '24px !important' }}>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <ScheduleIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">Schedule</Typography>
                    </Box>
                    <DateTimePicker
                        label="Scheduled Date & Time"
                        value={post?.scheduledFor ? dayjs(post?.scheduledFor) : dayjs()}
                        disabled={post?.status === 'published'}
                        onChange={(value) => handlePostChange({ ...post, scheduledFor: value.toISOString() })}
                        sx={{ width: '100%' }}
                        slotProps={{
                            textField: {
                                fullWidth: true
                            }
                        }}
                    />
                    
                    {post?.scheduledFor && dayjs(post.scheduledFor).isValid() && dayjs(post.scheduledFor).isBefore(dayjs()) && post?.status !== 'published' && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            Scheduled date must be in the future
                        </Alert>
                    )}
                    {!post?.scheduledFor && post?.status !== 'published' && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            Scheduled date is required
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Images Section */}
            <Card sx={{ mb: 2, width: '100%' }}>
                <CardContent sx={{ padding: '24px !important' }}>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <ImageIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">Images</Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {(photoSrcs ?? []).map((photoSrc, index) => (
                            <img 
                                key={index}
                                src={photoSrc} 
                                style={{ maxWidth: '100%', maxHeight: '300px', height: 'auto', objectFit: 'contain', flex: '1 1 auto', minWidth: '200px' }} 
                                alt='Content to post' 
                            />
                        ))}
                    </Box>

                    <Box display="flex" gap={2} justifyContent="center">
                        <Button 
                            component="label" 
                            variant="contained" 
                            startIcon={<CloudUploadIcon />} 
                            disabled={post?.status === 'published'}
                        >
                            Change Image(s)
                            <VisuallyHiddenInput type="file" onChange={changeImagesInternal} multiple />
                        </Button>
                        <Button 
                            variant="contained" 
                            startIcon={<DeleteIcon />} 
                            onClick={removeImagesInternal} 
                            disabled={post?.status === 'published'}
                            color="error"
                        >
                            Remove Image(s)
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Box display="flex" justifyContent="center" gap={2} sx={{ mt: 3, width: '100%' }}>
                {handleCancel && (
                    <Button 
                        type="button"
                        variant="outlined"
                        onClick={() => {
                            if (changed) {
                                setCancelDialogOpen(true);
                            } else {
                                handleCancel();
                            }
                        }}
                        size="large"
                    >
                        Cancel
                    </Button>
                )}
                <Button 
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!changed || !post?.text || post.text.length > 300 || !post?.scheduledFor || (post?.status !== 'published' && dayjs(post?.scheduledFor).isBefore(dayjs()))} 
                    size="large"
                >
                    Save
                </Button>
            </Box>

            <ConfirmDialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                onConfirm={() => handleCancel()}
                title="Discard Changes?"
                message="You have unsaved changes. Are you sure you want to cancel? All changes will be lost."
                confirmText="Discard Changes"
                cancelText="Continue Editing"
                confirmColor="error"
                autoFocusConfirm={true}
            />
        </form >
    );
};

export default PostEditForm;
